import { router, useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import FileInputField from '@/components/inputs/FileInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { containsName, containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { FileToUpload } from '@/utils/uploadMultipleToFireStorage';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

const NpoPLWiki = () => {
  const { pl, npoId } = useLocalSearchParams<{ pl: string; npoId: string }>();
  const { mutateAsync: nonProfitUpdateWiki, isPending: nonProfitIsPending } = useUpdateNonProfitOrgWiki();

  const { showError, showSuccess } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);

  const initialData = {
    pL: typeof pl === 'string' ? safeJsonParseArray(pl)[0] : ''
  };

  const onSubmit = async (data: { pL?: string | null | undefined }) => {
    try {
      setIsLoading(true);
      let filesToUpload: FileToUpload | undefined = undefined;
      if (data.pL && !containsSection(data.pL, 'pl')) {
        filesToUpload = { uri: data.pL, name: `profit_loss.${new Date().toISOString()}` };
      }

      if (filesToUpload !== undefined) {
        await uploadToFireStorage(`non-profits-orgs/${npoId}/pl/`, filesToUpload, (progress) => {
          setFileProgress(progress);
        }).then((storagedFiles) => {
          const pl = containsName(storagedFiles.metadata.name, 'profit_loss') ? [storagedFiles.metadata.fullPath] : [];

          nonProfitUpdateWiki(
            {
              npoId: npoId ?? '',
              plStatement: data.pL && pl.length === 0 ? undefined : pl
            },
            {
              onSuccess: () => {
                setNpoId(npoId ?? '');
                setVisible(true);
              },
              onError: (e) => {
                showError({ message: e.message });
                setIsLoading(false);
                setFileProgress(undefined);
                console.error('Error', e);
              }
            }
          );
        });
      } else {
        nonProfitUpdateWiki(
          {
            npoId: npoId ?? '',
            plStatement: data.pL ? undefined : []
          },
          {
            onSuccess: () => {
              showSuccess({
                title: i18n.t('end_flow.success_title'),
                message: i18n.t('npo_details.update_message')
              });
              router.back();
            },
            onError: (e) => {
              showError({ message: e.message });
              setIsLoading(false);
              setFileProgress(undefined);
              console.error('Error', e);
            }
          }
        );
      }
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      console.error(e);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<{ pL?: string | null | undefined }>({
    defaultValues: initialData,
    mode: 'onSubmit'
  });

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <OnboardingSection label={i18n.t('sign_in.nonProfit.pl.pl_section')}>
          <FileInputField control={control} name="pL" progress={fileProgress} />
        </OnboardingSection>

        <BrandedButton
          style={styles.doneButton}
          loading={nonProfitIsPending || isLoading}
          disabled={!isValid && submitCount > 0}
          onPress={handleSubmit(onSubmit)}>
          {i18n.t('mix.done')}
        </BrandedButton>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 25
  },

  doneButton: {
    width: '100%'
  }
});

export default NpoPLWiki;
